import { useState, useEffect } from "react";
import { PolarEmbedCheckout } from "@polar-sh/checkout/embed";
import { capture } from "@/lib/analytics";
import { toast } from "@/hooks/use-toast";

export function useDownload(animationId: string) {
  const [downloading, setDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [purchased, setPurchased] = useState(false);

  useEffect(() => {
    if (!animationId) return;
    const checkStatus = async () => {
      try {
        const res = await fetch(`/api/download/${animationId}/status`);
        if (res.ok) {
          const { purchased } = (await res.json()) as { purchased: boolean };
          setPurchased(purchased);
        }
      } catch {
        // ignore
      }
    };
    void checkStatus();
  }, [animationId]);

  const handleDownload = async () => {
    capture("download_initiated", { animation_id: animationId });
    setDownloading(true);
    try {
      const res = await fetch(`/api/download/${animationId}`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? "Failed to create checkout");
      }
      const data = (await res.json()) as { checkout_url?: string; download_url?: string };
      
      if (data.download_url) {
        capture("download_already_purchased", { animation_id: animationId });
        setDownloadUrl(data.download_url);
        setDownloading(false);
        return;
      }

      if (!data.checkout_url) {
        throw new Error("No checkout URL returned");
      }

      capture("download_checkout_created", { animation_id: animationId });

      const isDark = document.documentElement.classList.contains("dark");
      const checkout = await PolarEmbedCheckout.create(data.checkout_url, { theme: isDark ? "dark" : "light" });

      setDownloading(false);

      checkout.addEventListener("success", (e) => {
        capture("download_completed", { animation_id: animationId });
        // successURL → our backend → 302 → presigned R2 URL with Content-Disposition: attachment
        setDownloadUrl(e.detail.successURL);
        checkout.close();
      });
    } catch (err) {
      capture("download_failed", {
        animation_id: animationId,
        error: err instanceof Error ? err.message : String(err),
      });
      toast({
        variant: "destructive",
        title: "Download failed",
        description: err instanceof Error ? err.message : "Could not start checkout.",
      });
      setDownloading(false);
    }
  };

  return { downloading, handleDownload, downloadUrl, purchased };
}
